/**
 * WebOs Shell
 *
 * Terminal interactif avec xterm.js et commandes built-in.
 */

import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import { createLogger } from '../utils/debug.js';
import { getVfsUrl } from '../utils/paths.js';
import { Commands } from './commands.js';
import { ManPages } from './manpages.js';
import { getProcessManager } from '../kernel/process-manager.js';
import { getSystemWindowManager } from '../kernel/system-window-manager.js';

const log = createLogger('Shell');

// Thème Catppuccin Mocha pour xterm
const CATPPUCCIN_THEME = {
  background: '#1e1e2e',
  foreground: '#cdd6f4',
  cursor: '#f5e0dc',
  cursorAccent: '#1e1e2e',
  selectionBackground: '#585b70',
  selectionForeground: '#cdd6f4',

  black: '#45475a',
  red: '#f38ba8',
  green: '#a6e3a1',
  yellow: '#f9e2af',
  blue: '#89b4fa',
  magenta: '#cba6f7',
  cyan: '#94e2d5',
  white: '#bac2de',

  brightBlack: '#585b70',
  brightRed: '#f38ba8',
  brightGreen: '#a6e3a1',
  brightYellow: '#f9e2af',
  brightBlue: '#89b4fa',
  brightMagenta: '#cba6f7',
  brightCyan: '#94e2d5',
  brightWhite: '#a6adc8',
};

/**
 * Classe Shell principale
 */
export class Shell {
  constructor(container, fs, user = null, options = {}) {
    this.container = container;
    this.fs = fs;
    this.user = user;
    this.options = options; // { existingTerminal, fitAddon, skipWelcome, skipAutostart }

    // Déterminer les infos utilisateur
    const username = user?.username || 'user';
    const home = user?.home || `/home/${username}`;

    // État du shell
    this.running = true;
    this.cwd = home;
    this.previousCwd = null;
    this.env = {
      USER: username,
      HOME: home,
      PWD: home,
      SHELL: '/bin/websh',
      TERM: 'xterm-256color',
      PATH: `${home}/.local/apps:/usr/bin:/bin`,
      PS1: '\\u@webos:\\w$ ',
      UID: String(user?.uid ?? 1000),
    };

    // Historique
    this.history = [];
    this.historyIndex = -1;
    this.maxHistory = 1000;

    // Ligne courante
    this.currentLine = '';
    this.cursorPos = 0;

    // Terminal xterm.js
    this.terminal = null;
    this.fitAddon = null;
    this.inputDisposable = null;  // Pour pouvoir désactiver le handler d'entrée

    // Commandes et manuels
    this.commands = new Commands(this);
    this.manPages = new ManPages(fs);

    log.debug('Shell created');
  }

  /**
   * Initialise le shell
   */
  async init() {
    log.debug('Initializing shell...');

    // Obtenir le ProcessManager
    this.processManager = getProcessManager();

    // Initialiser les manuels
    await this.manPages.init();

    // Configurer les commandes avec les dépendances
    this.commands.configure(this.fs, this.manPages);

    // Utiliser un terminal existant ou en créer un nouveau
    if (this.options.existingTerminal) {
      // Réutiliser un terminal existant (ex: depuis DeskWeb)
      this.terminal = this.options.existingTerminal;
      this.fitAddon = this.options.fitAddon;
      log.debug('Using existing terminal');
    } else {
      // Créer le terminal xterm.js
      this.terminal = new Terminal({
        theme: CATPPUCCIN_THEME,
        fontFamily: '"Cascadia Code", "Fira Code", "Consolas", monospace',
        fontSize: 14,
        lineHeight: 1.2,
        cursorBlink: true,
        cursorStyle: 'block',
        allowTransparency: false,
        scrollback: 10000,
      });

      this.fitAddon = new FitAddon();
      this.terminal.loadAddon(this.fitAddon);

      // Monter le terminal dans le container
      this.terminal.open(this.container);

      // Debug : dimensions du container avant fit
      const rect = this.container.getBoundingClientRect();
      log.debug(`Container before fit: ${rect.width}x${rect.height}`);

      // Fit initial
      this.fitAddon.fit();
      log.debug(`Terminal after fit: ${this.terminal.cols}x${this.terminal.rows} chars`);

      // Fit avec délais pour s'assurer que le layout est complet
      setTimeout(() => {
        this.fitAddon.fit();
        log.debug(`Terminal after 0ms: ${this.terminal.cols}x${this.terminal.rows} chars`);
      }, 0);

      setTimeout(() => {
        this.fitAddon.fit();
        log.debug(`Terminal after 100ms: ${this.terminal.cols}x${this.terminal.rows} chars`);
      }, 100);

      setTimeout(() => {
        this.fitAddon.fit();
        log.debug(`Terminal after 500ms: ${this.terminal.cols}x${this.terminal.rows} chars`);
      }, 500);

      // Gérer le redimensionnement
      const resizeObserver = new ResizeObserver(() => {
        this.fitAddon.fit();
        log.debug(`Terminal after resize: ${this.terminal.cols}x${this.terminal.rows} chars`);
      });
      resizeObserver.observe(this.container);

      // Fit aussi quand la fenêtre change
      window.addEventListener('resize', () => {
        this.fitAddon.fit();
      });
    }

    // Écouter les entrées utilisateur
    this.inputDisposable = this.terminal.onData(data => this.handleInput(data));

    // Afficher le message de bienvenue (sauf si désactivé)
    if (!this.options.skipWelcome) {
      this.printWelcome();
    }

    // Vérifier s'il y a une commande autostart (sauf si désactivé)
    if (!this.options.skipAutostart) {
      await this.checkAutostart();
    }

    // Afficher le prompt
    this.printPrompt();

    log.info('Shell initialized');
  }

  /**
   * Vérifie s'il y a des apps à exécuter au démarrage
   */
  async checkAutostart() {
    // 1. Vérifier le nouveau format JSON (apps comme DeskWeb)
    const jsonAutostartPath = `${this.env.HOME}/.config/webos/autostart.json`;
    log.debug(`Checking JSON autostart at: ${jsonAutostartPath}`);

    try {
      const content = await this.fs.readFile(jsonAutostartPath);
      const config = JSON.parse(content);

      if (config.apps && Array.isArray(config.apps) && config.apps.length > 0) {
        log.info(`Autostart apps found: ${config.apps.join(', ')}`);

        for (const appName of config.apps) {
          this.terminal.write(`\r\n\x1b[33m[Autostart]\x1b[0m Lancement de ${appName}...\r\n`);

          // Lancer chaque app en arrière-plan sans bloquer
          this.executeCommand(appName).catch(err => {
            log.error(`Autostart app ${appName} failed:`, err);
            this.terminal.write(`\x1b[31mErreur:\x1b[0m ${appName} n'a pas pu démarrer\r\n`);
          });
        }

        // Petit délai avant d'afficher le prompt
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    } catch (err) {
      // Fichier JSON n'existe pas, essayer l'ancien format
      log.debug('No JSON autostart config:', err.message);
    }

    // 2. Vérifier l'ancien format (fichier texte avec une commande)
    const legacyAutostartPath = `${this.env.HOME}/.config/shell/autostart`;
    log.debug(`Checking legacy autostart at: ${legacyAutostartPath}`);

    try {
      const command = await this.fs.readFile(legacyAutostartPath);
      log.debug(`Legacy autostart file content:`, command);

      if (command && typeof command === 'string' && command.trim()) {
        const cmd = command.trim();
        log.info(`Legacy autostart command found: ${cmd}`);
        this.terminal.write(`\r\n\x1b[33m[Autostart]\x1b[0m Lancement de ${cmd}...\r\n\r\n`);

        // Exécuter la commande après un court délai
        setTimeout(async () => {
          try {
            await this.executeCommand(cmd);
          } catch (execErr) {
            log.error('Legacy autostart command execution failed:', execErr);
          }
        }, 500);
      } else {
        log.debug('Legacy autostart file empty or invalid');
      }
    } catch (err) {
      // Pas de fichier autostart legacy, c'est normal
      log.debug('No legacy autostart file found:', err.message);
    }
  }

  /**
   * Affiche le message de bienvenue
   */
  printWelcome() {
    const version = '0.9.0';
    const welcome = `\r
\x1b[1;34m░██       ░██            ░██          ░██████                                              ░██                                           \x1b[0m\r
\x1b[1;34m░██       ░██            ░██         ░██   ░██                                                                                           \x1b[0m\r
\x1b[1;34m░██  ░██  ░██  ░███████  ░████████  ░██     ░██  ░███████            ░████████   ░███████  ░██          ░██    ░██ ░██    ░██ ░█████████ \x1b[0m\r
\x1b[1;34m░██ ░████ ░██ ░██    ░██ ░██    ░██ ░██     ░██ ░██                  ░██    ░██ ░██        ░██           ░██  ░██  ░██    ░██      ░███  \x1b[0m\r
\x1b[1;34m░██░██ ░██░██ ░█████████ ░██    ░██ ░██     ░██  ░███████            ░██    ░██  ░███████  ░██            ░█████   ░██    ░██    ░███    \x1b[0m\r
\x1b[1;34m░████   ░████ ░██        ░███   ░██  ░██   ░██         ░██           ░██    ░██        ░██ ░██           ░██  ░██  ░██   ░███  ░███      \x1b[0m\r
\x1b[1;34m░███     ░███  ░███████  ░██░█████    ░██████    ░███████     ░██    ░██    ░██  ░███████  ░██   ░██    ░██    ░██  ░█████░██ ░█████████ \x1b[0m\r
\x1b[1;34m                                                                                                                          ░██            \x1b[0m\r
\x1b[1;34m                                                                                                                    ░███████             \x1b[0m v${version}\r
\r
 Tapez \x1b[1;33mhelp\x1b[0m pour la liste des commandes\r
 Tapez \x1b[1;33mman <cmd>\x1b[0m pour le manuel d'une commande\r
\r
`;
    this.terminal.write(welcome);
  }

  /**
   * Affiche le prompt
   */
  printPrompt() {
    const user = this.env.USER;
    const host = 'webos';

    // Remplacer /home/user par ~ dans le chemin
    let displayPath = this.cwd;
    if (displayPath.startsWith(this.env.HOME)) {
      displayPath = '~' + displayPath.slice(this.env.HOME.length);
    }

    const prompt = `\x1b[1;32m${user}@${host}\x1b[0m:\x1b[1;34m${displayPath}\x1b[0m$ `;
    this.terminal.write(prompt);
  }

  /**
   * Écrit du texte dans le terminal
   */
  print(text) {
    this.terminal.write(text.replace(/\n/g, '\r\n'));
  }

  /**
   * Gère les entrées clavier
   */
  handleInput(data) {
    if (!this.running) return;

    // Codes spéciaux
    const code = data.charCodeAt(0);

    // Entrée (exécuter la commande)
    if (data === '\r') {
      this.terminal.write('\r\n');
      this.executeCommand();
      return;
    }

    // Backspace
    if (data === '\x7f' || data === '\b') {
      if (this.cursorPos > 0) {
        this.currentLine =
          this.currentLine.slice(0, this.cursorPos - 1) +
          this.currentLine.slice(this.cursorPos);
        this.cursorPos--;
        this.refreshLine();
      }
      return;
    }

    // Ctrl+C (annuler)
    if (data === '\x03') {
      this.terminal.write('^C\r\n');
      this.currentLine = '';
      this.cursorPos = 0;
      this.historyIndex = -1;
      this.printPrompt();
      return;
    }

    // Ctrl+L (clear)
    if (data === '\x0c') {
      this.terminal.clear();
      this.printPrompt();
      this.terminal.write(this.currentLine);
      return;
    }

    // Ctrl+A (début de ligne)
    if (data === '\x01') {
      this.cursorPos = 0;
      this.refreshLine();
      return;
    }

    // Ctrl+E (fin de ligne)
    if (data === '\x05') {
      this.cursorPos = this.currentLine.length;
      this.refreshLine();
      return;
    }

    // Ctrl+U (effacer avant curseur)
    if (data === '\x15') {
      this.currentLine = this.currentLine.slice(this.cursorPos);
      this.cursorPos = 0;
      this.refreshLine();
      return;
    }

    // Ctrl+K (effacer après curseur)
    if (data === '\x0b') {
      this.currentLine = this.currentLine.slice(0, this.cursorPos);
      this.refreshLine();
      return;
    }

    // Flèches et séquences d'échappement
    if (data.startsWith('\x1b[')) {
      const seq = data.slice(2);

      // Flèche haut (historique précédent = remonter dans l'historique)
      if (seq === 'A') {
        this.navigateHistory(1);
        return;
      }

      // Flèche bas (historique suivant = redescendre vers le présent)
      if (seq === 'B') {
        this.navigateHistory(-1);
        return;
      }

      // Flèche droite
      if (seq === 'C') {
        if (this.cursorPos < this.currentLine.length) {
          this.cursorPos++;
          this.terminal.write(data);
        }
        return;
      }

      // Flèche gauche
      if (seq === 'D') {
        if (this.cursorPos > 0) {
          this.cursorPos--;
          this.terminal.write(data);
        }
        return;
      }

      // Home
      if (seq === 'H' || seq === '1~') {
        this.cursorPos = 0;
        this.refreshLine();
        return;
      }

      // End
      if (seq === 'F' || seq === '4~') {
        this.cursorPos = this.currentLine.length;
        this.refreshLine();
        return;
      }

      // Delete
      if (seq === '3~') {
        if (this.cursorPos < this.currentLine.length) {
          this.currentLine =
            this.currentLine.slice(0, this.cursorPos) +
            this.currentLine.slice(this.cursorPos + 1);
          this.refreshLine();
        }
        return;
      }

      return;
    }

    // Tab (auto-complétion basique)
    if (data === '\t') {
      this.autoComplete();
      return;
    }

    // Caractère imprimable
    if (code >= 32) {
      this.currentLine =
        this.currentLine.slice(0, this.cursorPos) +
        data +
        this.currentLine.slice(this.cursorPos);
      this.cursorPos += data.length;
      this.refreshLine();
    }
  }

  /**
   * Calcule la longueur visible du prompt (sans codes ANSI)
   */
  getPromptLength() {
    const user = this.env.USER;
    const host = 'webos';
    let displayPath = this.cwd;
    if (displayPath.startsWith(this.env.HOME)) {
      displayPath = '~' + displayPath.slice(this.env.HOME.length);
    }
    // user@host:path$  (avec espace final)
    return user.length + 1 + host.length + 1 + displayPath.length + 2;
  }

  /**
   * Rafraîchit l'affichage de la ligne courante
   */
  refreshLine() {
    const cols = this.terminal.cols;
    const promptLen = this.getPromptLength();
    const totalLen = promptLen + this.currentLine.length;

    // Calculer combien de lignes le contenu occupe actuellement
    const currentLines = Math.ceil(totalLen / cols) || 1;

    // Remonter au début de la zone d'édition
    if (currentLines > 1) {
      // Remonter de (currentLines - 1) lignes
      this.terminal.write(`\x1b[${currentLines - 1}A`);
    }

    // Aller au début de la ligne et effacer tout jusqu'à la fin de l'écran
    this.terminal.write('\r\x1b[J');

    // Réécrire le prompt et la ligne
    this.printPromptRaw();
    this.terminal.write(this.currentLine);

    // Calculer la position du curseur
    const cursorOffset = promptLen + this.cursorPos;
    const cursorRow = Math.floor(cursorOffset / cols);
    const cursorCol = cursorOffset % cols;

    // Calculer où on est maintenant
    const endOffset = promptLen + this.currentLine.length;
    const endRow = Math.floor(endOffset / cols);
    const endCol = endOffset % cols;

    // Repositionner le curseur
    const rowDiff = endRow - cursorRow;
    const colDiff = endCol - cursorCol;

    if (rowDiff > 0) {
      this.terminal.write(`\x1b[${rowDiff}A`);
    }

    if (colDiff > 0) {
      this.terminal.write(`\x1b[${colDiff}D`);
    } else if (colDiff < 0) {
      this.terminal.write(`\x1b[${-colDiff}C`);
    }
  }

  /**
   * Affiche le prompt sans retour chariot
   */
  printPromptRaw() {
    const user = this.env.USER;
    const host = 'webos';

    let displayPath = this.cwd;
    if (displayPath.startsWith(this.env.HOME)) {
      displayPath = '~' + displayPath.slice(this.env.HOME.length);
    }

    const prompt = `\x1b[1;32m${user}@${host}\x1b[0m:\x1b[1;34m${displayPath}\x1b[0m$ `;
    this.terminal.write(prompt);
  }

  /**
   * Navigue dans l'historique
   */
  navigateHistory(direction) {
    if (this.history.length === 0) return;

    const newIndex = this.historyIndex + direction;

    if (newIndex < -1) return;
    if (newIndex >= this.history.length) return;

    this.historyIndex = newIndex;

    if (newIndex === -1) {
      this.currentLine = '';
    } else {
      this.currentLine = this.history[this.history.length - 1 - newIndex];
    }

    this.cursorPos = this.currentLine.length;
    this.refreshLine();
  }

  /**
   * Auto-complétion basique
   */
  async autoComplete() {
    const parts = this.currentLine.slice(0, this.cursorPos).split(/\s+/);
    const lastPart = parts[parts.length - 1];

    if (!lastPart) return;

    // Déterminer le chemin à compléter
    let dirPath, prefix;

    if (lastPart.includes('/')) {
      const lastSlash = lastPart.lastIndexOf('/');
      dirPath = this.commands.resolvePath(lastPart.slice(0, lastSlash) || '/');
      prefix = lastPart.slice(lastSlash + 1);
    } else if (parts.length === 1) {
      // Complétion de commande
      const matches = Array.from(this.commands.commands.keys())
        .filter(cmd => cmd.startsWith(lastPart));

      if (matches.length === 1) {
        const completion = matches[0].slice(lastPart.length);
        this.insertText(completion + ' ');
      } else if (matches.length > 1) {
        this.terminal.write('\r\n');
        this.terminal.write(matches.join('  ') + '\r\n');
        this.printPrompt();
        this.terminal.write(this.currentLine);
      }
      return;
    } else {
      dirPath = this.cwd;
      prefix = lastPart;
    }

    try {
      const entries = await this.fs.readdir(dirPath);
      const matches = entries.filter(e => e.name.startsWith(prefix));

      if (matches.length === 1) {
        const match = matches[0];
        const completion = match.name.slice(prefix.length);
        const suffix = match.type === 'directory' ? '/' : ' ';
        this.insertText(completion + suffix);
      } else if (matches.length > 1) {
        // Trouver le préfixe commun
        const names = matches.map(m => m.name);
        let common = names[0];

        for (const name of names) {
          let i = 0;
          while (i < common.length && i < name.length && common[i] === name[i]) {
            i++;
          }
          common = common.slice(0, i);
        }

        if (common.length > prefix.length) {
          this.insertText(common.slice(prefix.length));
        } else {
          // Afficher les possibilités
          this.terminal.write('\r\n');
          this.terminal.write(names.map(n => {
            const entry = matches.find(m => m.name === n);
            if (entry.type === 'directory') {
              return `\x1b[1;34m${n}\x1b[0m`;
            }
            return n;
          }).join('  ') + '\r\n');
          this.printPrompt();
          this.terminal.write(this.currentLine);
        }
      }
    } catch (err) {
      log.debug('Autocomplete error:', err.message);
    }
  }

  /**
   * Insère du texte à la position courante
   */
  insertText(text) {
    this.currentLine =
      this.currentLine.slice(0, this.cursorPos) +
      text +
      this.currentLine.slice(this.cursorPos);
    this.cursorPos += text.length;
    this.refreshLine();
  }

  /**
   * Exécute la commande courante (avec support des pipes et redirections)
   */
  async executeCommand() {
    const line = this.currentLine.trim();
    this.currentLine = '';
    this.cursorPos = 0;
    this.historyIndex = -1;

    // Ignorer les lignes vides
    if (!line) {
      this.printPrompt();
      return;
    }

    // Ajouter à l'historique
    if (this.history[this.history.length - 1] !== line) {
      this.history.push(line);
      if (this.history.length > this.maxHistory) {
        this.history.shift();
      }
    }

    // Parser le pipeline (pipes et redirections)
    const pipeline = this.parsePipeline(line);

    log.debug('Executing pipeline:', pipeline);

    try {
      // Exécuter le pipeline
      const result = await this.executePipeline(pipeline);

      if (result.output) {
        this.print(result.output);
      }

      if (result.error) {
        this.print(`\x1b[1;31m${result.error}\x1b[0m\n`);
      }
    } catch (err) {
      this.print(`\x1b[1;31m${err.message}\x1b[0m\n`);
    }

    // Afficher le prompt suivant
    if (this.running) {
      this.printPrompt();
    }
  }

  /**
   * Parse une ligne de commande (simple, sans pipes)
   */
  parseCommandLine(line) {
    const tokens = this.tokenize(line);
    // Filtrer les opérateurs pour le parsing simple
    const filtered = tokens.filter(t => !['|', '>', '>>', '<'].includes(t));
    return {
      command: filtered[0] || '',
      args: filtered.slice(1)
    };
  }

  /**
   * Tokenize une ligne en respectant les quotes et identifiant les opérateurs
   * @param {string} line
   * @returns {string[]}
   */
  tokenize(line) {
    const tokens = [];
    let current = '';
    let inQuote = false;
    let quoteChar = null;
    let escape = false;

    // Opérateurs à reconnaître (ordre important: >> avant >)
    const operators = ['>>', '|', '>', '<'];

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (escape) {
        current += char;
        escape = false;
        continue;
      }

      if (char === '\\' && !inQuote) {
        escape = true;
        continue;
      }

      if (inQuote) {
        if (char === quoteChar) {
          inQuote = false;
          quoteChar = null;
        } else {
          current += char;
        }
        continue;
      }

      if (char === '"' || char === "'") {
        inQuote = true;
        quoteChar = char;
        continue;
      }

      // Vérifier les opérateurs (seulement hors quotes)
      let isOperator = false;
      for (const op of operators) {
        if (line.slice(i, i + op.length) === op) {
          // Flush le token courant
          if (current) {
            tokens.push(current);
            current = '';
          }
          tokens.push(op);
          i += op.length - 1;
          isOperator = true;
          break;
        }
      }

      if (!isOperator) {
        if (char === ' ' || char === '\t') {
          if (current) {
            tokens.push(current);
            current = '';
          }
        } else {
          current += char;
        }
      }
    }

    if (current) {
      tokens.push(current);
    }

    return tokens;
  }

  /**
   * Parse une ligne de commande complète avec pipes et redirections
   * @param {string} line
   * @returns {Object} { segments, inputFile, outputFile, appendMode }
   */
  parsePipeline(line) {
    const tokens = this.tokenize(line);

    const segments = [];
    let currentSegment = { command: null, args: [] };
    let inputFile = null;
    let outputFile = null;
    let appendMode = false;

    let i = 0;
    while (i < tokens.length) {
      const token = tokens[i];

      if (token === '|') {
        // Fin du segment courant, début d'un nouveau
        if (currentSegment.command) {
          segments.push(currentSegment);
          currentSegment = { command: null, args: [] };
        }
      } else if (token === '>') {
        // Redirection output (écrasement)
        if (i + 1 < tokens.length) {
          outputFile = tokens[++i];
          appendMode = false;
        }
      } else if (token === '>>') {
        // Redirection output (ajout)
        if (i + 1 < tokens.length) {
          outputFile = tokens[++i];
          appendMode = true;
        }
      } else if (token === '<') {
        // Redirection input
        if (i + 1 < tokens.length) {
          inputFile = tokens[++i];
        }
      } else {
        // Token normal
        if (!currentSegment.command) {
          currentSegment.command = token;
        } else {
          currentSegment.args.push(token);
        }
      }
      i++;
    }

    // Ajouter le segment final
    if (currentSegment.command) {
      segments.push(currentSegment);
    }

    return { segments, inputFile, outputFile, appendMode };
  }

  /**
   * Exécute un pipeline complet (commandes avec pipes et redirections)
   * @param {Object} pipeline - { segments, inputFile, outputFile, appendMode }
   * @returns {Promise<Object>} Résultat { exitCode, output?, error? }
   */
  async executePipeline(pipeline) {
    const { segments, inputFile, outputFile, appendMode } = pipeline;

    if (segments.length === 0) {
      return { exitCode: 0 };
    }

    // Cas simple: une seule commande sans redirections
    if (segments.length === 1 && !inputFile && !outputFile) {
      return await this.executeSegment(segments[0], null);
    }

    // Récupérer l'input initial depuis un fichier si spécifié
    let currentInput = null;
    if (inputFile) {
      const path = this.commands.resolvePath(inputFile);
      try {
        currentInput = await this.fs.readFile(path);
      } catch (err) {
        return { exitCode: 1, error: `${inputFile}: ${err.message}` };
      }
    }

    // Exécuter chaque segment en séquence
    let lastResult = { exitCode: 0, output: '' };

    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      lastResult = await this.executeSegment(segment, currentInput);

      // Si la commande échoue et il y a d'autres étapes, on arrête
      if (lastResult.exitCode !== 0 && i < segments.length - 1) {
        break;
      }

      // L'output devient l'input pour l'étape suivante
      currentInput = lastResult.output || '';
    }

    // Gérer la redirection de sortie
    if (outputFile) {
      const path = this.commands.resolvePath(outputFile);
      const content = lastResult.output || '';

      try {
        if (appendMode) {
          const existing = await this.fs.readFile(path).catch(() => '');
          await this.fs.writeFile(path, existing + content);
        } else {
          await this.fs.writeFile(path, content);
        }
        // Ne pas afficher dans le terminal quand on redirige
        return { exitCode: lastResult.exitCode };
      } catch (err) {
        return { exitCode: 1, error: `${outputFile}: ${err.message}` };
      }
    }

    return lastResult;
  }

  /**
   * Exécute un segment de pipeline (une commande)
   * @param {Object} segment - { command, args }
   * @param {string|null} stdin - Input depuis l'étape précédente
   * @returns {Promise<Object>} Résultat { exitCode, output?, error? }
   */
  async executeSegment(segment, stdin) {
    const { command, args } = segment;

    // Commande built-in
    if (this.commands.has(command)) {
      return await this.commands.execute(command, args, stdin);
    }

    // Commande externe
    return await this.runExternalCommand(command, args, stdin);
  }

  /**
   * Exécute une commande et retourne sa sortie (API pour IPC REQ_EXEC)
   * @param {string} command - Nom de la commande
   * @param {string[]} args - Arguments
   * @param {Object} options - { cwd, env }
   * @returns {Promise<Object>} { output, exitCode, error }
   */
  async execCommand(command, args = [], options = {}) {
    // Sauvegarder le contexte actuel
    const savedCwd = this.cwd;
    const savedEnv = { ...this.env };

    try {
      // Appliquer le contexte temporaire si fourni
      if (options.cwd) {
        this.cwd = options.cwd;
        this.env.PWD = options.cwd;
      }
      if (options.env) {
        Object.assign(this.env, options.env);
      }

      // Exécuter la commande
      const result = await this.executeSegment({ command, args }, null);

      return {
        output: result.output || '',
        exitCode: result.exitCode ?? 0,
        error: result.error || null
      };
    } finally {
      // Restaurer le contexte
      this.cwd = savedCwd;
      this.env = savedEnv;
    }
  }

  /**
   * Cherche une commande dans le PATH
   * Retourne { appPath, actualCommand } ou null
   * actualCommand peut différer de command si c'est un alias
   */
  async findInPath(command) {
    const pathDirs = this.env.PATH.split(':');

    // 1. Chercher directement le dossier de la commande
    for (const dir of pathDirs) {
      const appPath = `${dir}/${command}`;

      try {
        const stat = await this.fs.stat(appPath);
        if (stat.type !== 'directory') continue;

        const manifestPath = `${appPath}/manifest.json`;
        if (await this.fs.exists(manifestPath)) {
          return { appPath, actualCommand: command, aliasOf: null };
        }
      } catch (err) {
        continue;
      }
    }

    // 2. Chercher dans les aliases (commandes fournies par d'autres packages)
    try {
      const aliasesPath = '/var/lib/webpkg/aliases.json';
      if (await this.fs.exists(aliasesPath)) {
        const content = await this.fs.readFile(aliasesPath);
        const aliases = JSON.parse(content);

        if (aliases[command]) {
          const parentPkg = aliases[command];
          const appPath = `/usr/bin/${parentPkg}`;

          if (await this.fs.exists(`${appPath}/manifest.json`)) {
            return { appPath, actualCommand: command, aliasOf: parentPkg };
          }
        }
      }
    } catch (err) {
      log.debug('Alias lookup error:', err.message);
    }

    return null;
  }

  /**
   * Lance une commande externe (app installée dans PATH)
   * @param {string} command - Nom de la commande
   * @param {string[]} args - Arguments
   * @param {string|null} stdin - Input depuis un pipe (optionnel)
   */
  async runExternalCommand(command, args, stdin = null) {
    try {
      // Chercher l'app dans PATH
      const result = await this.findInPath(command);

      if (!result) {
        return { exitCode: 127, error: `${command}: commande introuvable` };
      }

      const { appPath, actualCommand, aliasOf } = result;

      log.debug(`Found external command: ${appPath}${aliasOf ? ` (alias of ${aliasOf})` : ''}`);

      // Charger le manifest
      const manifestContent = await this.fs.readFile(`${appPath}/manifest.json`);
      const manifest = JSON.parse(manifestContent);

      log.debug('Manifest:', manifest);

      // Valider le manifest
      if (!manifest.entry) {
        return { exitCode: 1, error: `${command}: manifest invalide (pas d'entry)` };
      }

      // Si c'est un alias, passer la commande originale en premier argument
      // pour que le package sache quelle sous-commande exécuter
      const effectiveArgs = aliasOf ? [actualCommand, ...args] : args;

      // Chemin complet du module de l'app
      const fullAppPath = `${appPath}/${manifest.entry}`;

      // Pour les TUI, GUI et Daemon : utiliser ProcessManager (le bootstrap charge l'app)
      // Ces types ne supportent pas stdin dans un pipeline
      if (manifest.type === 'tui') {
        if (stdin !== null) {
          return { exitCode: 1, error: `${command}: les applications TUI ne supportent pas stdin dans un pipeline` };
        }
        return await this.runTuiApp(effectiveArgs, manifest, fullAppPath);
      }

      if (manifest.type === 'gui') {
        if (stdin !== null) {
          return { exitCode: 1, error: `${command}: les applications GUI ne supportent pas stdin dans un pipeline` };
        }
        return await this.runGuiApp(effectiveArgs, manifest, fullAppPath);
      }

      if (manifest.type === 'daemon') {
        return await this.runDaemonApp(effectiveArgs, manifest, fullAppPath);
      }

      // Pour les CLI, on charge le module directement dans le Shell
      const moduleUrl = getVfsUrl(fullAppPath);
      log.debug(`Importing module: ${moduleUrl}`);

      const module = await import(moduleUrl);
      const AppClass = module.default || module[Object.keys(module)[0]];

      if (!AppClass || typeof AppClass !== 'function') {
        return { exitCode: 1, error: `${command}: module invalide (pas de classe exportée)` };
      }

      if (manifest.type === 'cli') {
        return await this.runCliApp(AppClass, effectiveArgs, manifest, stdin);
      } else {
        return { exitCode: 1, error: `${command}: type d'app inconnu (${manifest.type})` };
      }

    } catch (err) {
      log.error('External command error:', err);
      return { exitCode: 1, error: `${command}: ${err.message}` };
    }
  }

  /**
   * Lance une app TUI (prend le contrôle du terminal)
   * Utilise le ProcessManager pour isoler l'app dans un Worker
   * @param {string[]} args - Arguments de la commande
   * @param {Object} manifest - Manifest de l'application
   * @param {string} appPath - Chemin complet vers le fichier JS de l'app
   */
  async runTuiApp(args, manifest = {}, appPath = '') {
    const appName = manifest.name || 'tui-app';

    // Suspendre l'input du shell
    this.suspendInput();

    try {
      // Lancer le processus via ProcessManager
      // Le ProcessManager créera un Worker avec le bootstrap TUI
      const process = await this.processManager.spawn(appPath, args, {
        env: this.env,
        cwd: this.cwd,
        parentPid: 0 // Le shell est le parent
      });

      log.debug(`TUI app spawned: ${appName} (PID: ${process.pid})`);

      // Créer le bridge terminal pour la communication
      const terminalBridge = this.createTerminalBridge(process.worker);

      // Attendre la fin du processus
      const exitCode = await this.processManager.waitForProcess(process.pid);

      // Nettoyer le bridge
      terminalBridge.dispose();

      log.debug(`TUI app exited: ${appName} (code: ${exitCode})`);

      return { exitCode };

    } catch (err) {
      log.error('TUI app error:', err);
      return { exitCode: 1, error: err.message };
    } finally {
      // Réactiver l'input du shell
      this.resumeInput();
    }
  }

  /**
   * Crée un bridge entre le Worker et le terminal xterm.js
   * @param {Worker} worker
   * @returns {Object} Bridge avec méthode dispose()
   */
  createTerminalBridge(worker) {
    if (!worker) {
      return { dispose: () => {} };
    }

    // Handler pour les messages du Worker vers le terminal
    const messageHandler = (event) => {
      const message = event.data;

      if (message.type === 'terminal') {
        if (message.action === 'write') {
          this.terminal.write(message.data);
        }
      } else if (message.type === 'app_exit') {
        // L'app signale sa terminaison
        log.debug('App exit signal received, code:', message.code);
      }
    };

    worker.addEventListener('message', messageHandler);

    // Handler pour l'input utilisateur vers le Worker
    const inputDisposable = this.terminal.onData((data) => {
      worker.postMessage({
        type: 'terminal',
        action: 'input',
        data: data
      });
    });

    // Handler pour le redimensionnement
    const resizeHandler = () => {
      worker.postMessage({
        type: 'terminal',
        action: 'resize',
        cols: this.terminal.cols,
        rows: this.terminal.rows
      });
    };

    // Envoyer la taille initiale
    resizeHandler();

    // Observer les redimensionnements
    const resizeDisposable = this.terminal.onResize?.(resizeHandler);

    return {
      dispose: () => {
        worker.removeEventListener('message', messageHandler);
        inputDisposable.dispose();
        resizeDisposable?.dispose();
      }
    };
  }

  /**
   * Lance une app CLI (affiche le résultat et retourne)
   * Note: Les apps CLI ne sont pas isolées en Worker pour l'instant
   * @param {Function} AppClass - Classe de l'application
   * @param {string[]} args - Arguments
   * @param {Object} manifest - Manifest de l'app
   * @param {string|null} stdin - Input depuis un pipe (optionnel)
   */
  async runCliApp(AppClass, args, manifest = {}, stdin = null) {
    try {
      const app = new AppClass(this.fs);
      // Passer stdin si l'app le supporte (méthode run avec 2 args)
      const result = await app.run(args, stdin);
      return result || { exitCode: 0 };
    } catch (err) {
      log.error('CLI app error:', err);
      return { exitCode: 1, error: err.message };
    }
  }

  /**
   * Lance une app GUI (isolée dans une iframe via ProcessManager)
   * Ouvre l'app dans une fenêtre système (SystemWindowManager) ou en plein écran
   * @param {string[]} args - Arguments de la commande
   * @param {Object} manifest - Manifest de l'application
   * @param {string} appPath - Chemin complet vers le fichier JS de l'app
   */
  async runGuiApp(args, manifest = {}, appPath = '') {
    const appName = manifest.name || 'gui-app';
    const displayName = manifest.displayName || appName;

    try {
      // Lancer le processus via ProcessManager (créera une iframe)
      const process = await this.processManager.spawn(appPath, args, {
        env: this.env,
        cwd: this.cwd,
        parentPid: 0
      });

      log.debug(`GUI app spawned: ${appName} (PID: ${process.pid})`);

      // Mode fullscreen : l'app prend tout l'écran sans fenêtre (ex: DeskWeb)
      if (manifest.fullscreen === true) {
        return this.runFullscreenApp(process, manifest, displayName);
      }

      // Mode fenêtré : utiliser le SystemWindowManager
      const windowManager = getSystemWindowManager();

      // Convertir les dimensions si elles sont en string (ex: "100%")
      const width = this.parseDimension(manifest.windowWidth, window.innerWidth) || 600;
      const height = this.parseDimension(manifest.windowHeight, window.innerHeight) || 450;

      // Créer la fenêtre avec l'iframe comme contenu
      const win = windowManager.createWindow({
        title: displayName,
        pid: process.pid,
        iframe: process.iframe,
        width,
        height,
        minWidth: manifest.minWidth || 300,
        minHeight: manifest.minHeight || 200,
        onClose: () => {
          // Quand la fenêtre est fermée, terminer le processus
          log.debug(`Window closed, terminating PID ${process.pid}`);
          this.processManager.terminateProcess(process.pid, 0);
        }
      });

      log.debug(`GUI window created: ${win.id} for PID ${process.pid}`);

      // Configurer le callback de focus pour gérer l'input shell
      windowManager.onFocusChange = (focusedWindowId) => {
        if (focusedWindowId === null) {
          // Aucune fenêtre focusée, focus au shell
          this.terminal.focus();
        }
      };

      // Quand le processus se termine, fermer la fenêtre
      this.processManager.waitForProcess(process.pid).then((exitCode) => {
        log.debug(`GUI app finished: ${appName} (code: ${exitCode})`);
        // Fermer la fenêtre si elle existe encore
        const existingWin = windowManager.getWindowByPid(process.pid);
        if (existingWin) {
          windowManager.closeWindow(existingWin.id);
        }
      });

      // Ne pas attendre la fin du processus - le shell reste utilisable
      return { exitCode: 0 };

    } catch (err) {
      log.error('GUI app error:', err);
      return { exitCode: 1, error: err.message };
    }
  }

  /**
   * Parse une dimension qui peut être un nombre ou un pourcentage
   * @param {number|string} value - La valeur (ex: 600, "100%", "50%")
   * @param {number} reference - La référence pour les pourcentages (ex: window.innerWidth)
   * @returns {number|null}
   */
  parseDimension(value, reference) {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      if (value.endsWith('%')) {
        const percent = parseFloat(value);
        if (!isNaN(percent)) {
          return Math.floor(reference * percent / 100);
        }
      }
      const num = parseInt(value, 10);
      if (!isNaN(num)) return num;
    }
    return null;
  }

  /**
   * Lance une app GUI en mode plein écran (sans fenêtre)
   * Utilisé pour les DE comme DeskWeb qui prennent le contrôle total de l'affichage
   * @param {Object} process - Le processus spawné
   * @param {Object} manifest - Le manifest de l'app
   * @param {string} displayName - Nom d'affichage
   */
  async runFullscreenApp(process, manifest, displayName) {
    log.debug(`Running fullscreen app: ${displayName} (PID: ${process.pid})`);

    const iframe = process.iframe;

    // Positionner l'iframe en plein écran
    // Note: z-index 100 permet aux fenêtres d'apps (z-index 101+) d'apparaître au-dessus
    iframe.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      border: none;
      z-index: 100;
      display: block;
      background: #1e1e2e;
    `;

    // Masquer le container du shell
    if (this.container) {
      this.container.style.display = 'none';
    }

    // Quand le processus se termine, restaurer le shell
    this.processManager.waitForProcess(process.pid).then((exitCode) => {
      log.debug(`Fullscreen app finished: ${displayName} (code: ${exitCode})`);

      // Retirer l'iframe
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }

      // Restaurer le shell
      if (this.container) {
        this.container.style.display = '';
        this.terminal.focus();
      }
    });

    return { exitCode: 0 };
  }

  /**
   * Lance une app daemon (isolée dans un Worker via ProcessManager)
   * @param {string[]} args - Arguments de la commande
   * @param {Object} manifest - Manifest de l'application
   * @param {string} appPath - Chemin complet vers le fichier JS de l'app
   */
  async runDaemonApp(args, manifest = {}, appPath = '') {
    const appName = manifest.name || 'daemon';

    try {
      // Lancer le processus via ProcessManager (créera un Worker)
      const process = await this.processManager.spawn(appPath, args, {
        env: this.env,
        cwd: this.cwd,
        parentPid: 0
      });

      log.debug(`Daemon spawned: ${appName} (PID: ${process.pid})`);

      // Écouter la sortie du daemon (pour l'afficher dans le terminal)
      if (process.worker) {
        process.worker.onmessage = (event) => {
          if (event.data?.type === 'daemon_output') {
            this.terminal.write(event.data.data.replace(/\n/g, '\r\n'));
          }
        };
      }

      // Attendre la fin du daemon
      // Note: Les daemons peuvent tourner indéfiniment, donc on attend
      const exitCode = await this.processManager.waitForProcess(process.pid);

      log.debug(`Daemon finished: ${appName} (code: ${exitCode})`);

      return { exitCode };

    } catch (err) {
      log.error('Daemon error:', err);
      return { exitCode: 1, error: err.message };
    }
  }

  /**
   * Suspend l'écoute des entrées (pour laisser une app TUI prendre le contrôle)
   */
  suspendInput() {
    if (this.inputDisposable) {
      this.inputDisposable.dispose();
      this.inputDisposable = null;
      log.debug('Shell input suspended');
    }
  }

  /**
   * Reprend l'écoute des entrées
   */
  resumeInput() {
    if (!this.inputDisposable) {
      this.inputDisposable = this.terminal.onData(data => this.handleInput(data));
      log.debug('Shell input resumed');
    }
  }

  /**
   * Arrête le shell
   */
  destroy() {
    this.running = false;
    if (this.terminal) {
      this.terminal.dispose();
    }
    log.info('Shell destroyed');
  }
}

export default Shell;
